import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  CampaignService,
  CampaignStatistics,
  loadCampaignStatistics,
} from '../../services/campaign.service';

interface CampaignStat {
  title: string;
  value: string;
  color: string;
  iconColor: string;
  backgroundImage: string;
}

/**
 * Displays summarized campaign statistics as a set of statistic cards.
 * Provides a compact dashboard widget that loads campaign metrics (total, active, scheduled, completed)
 * from an injected CampaignService and maps them into CampaignStat objects for presentation.
 * Key properties:
 *   - campaignStats: CampaignStat[] - array of items rendered as statistic cards.
 *   - statistics: CampaignStatistics | null - raw statistics returned from the service.
 *   - loading: boolean - indicates whether statistics are being loaded.
 * Key methods:
 *   - loadStatistics(): Initiates loading by calling the shared loadCampaignStatistics utility with the injected CampaignService;
 *       it updates the statistics property and loading state via supplied callbacks.
 *   - updateStats(): Transforms the fetched statistics into the campaignStats array with titles, values, colors, icon colors, and background images.
 * Lifecycle hooks:
 *   - ngOnInit(): Lifecycle hook that triggers initial data load by calling loadStatistics() when the component initializes.
 * Notes:
 *   - This is a standalone component (selector: app-campaign-stats) that relies on CampaignService injection and external template/style files.
 * @component
 * @selector app-campaign-stats
 * @example
 *   <app-campaign-stats></app-campaign-stats>
 * @input None none - This component does not expose any @Input properties; it obtains data via the injected CampaignService.
 * @output None none - This component does not emit any @Output events; it is a presentational/statistics-only component.
 */
@Component({
  selector: 'app-campaign-stats',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './campaign-stats.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './campaign-stats.component.scss',
})
export class CampaignStatsComponent implements OnInit {
  campaignStats: CampaignStat[] = [
    {
      title: 'Total Campaigns',
      value: '-',
      color: '#73A0E9',
      iconColor: '#8FB7F6',
      backgroundImage: 'assets/dashboard/h-1.png',
    },
    {
      title: 'Active Campaigns',
      value: '-',
      color: '#B473E9',
      iconColor: '#B473E9',
      backgroundImage: 'assets/dashboard/h-2.png',
    },
    {
      title: 'Scheduled Campaigns',
      value: '-',
      color: '#E973C6',
      iconColor: '#E973C6',
      backgroundImage: 'assets/dashboard/h-3.png',
    },
    {
      title: 'Completed Campaigns',
      value: '-',
      color: '#E4A167',
      iconColor: '#EAA468',
      backgroundImage: 'assets/dashboard/h-4.png',
    },
  ];
  statistics: CampaignStatistics | null = null;
  loading = true;

  constructor(private campaignService: CampaignService) {}

  ngOnInit() {
    this.loadStatistics();
  }

  loadStatistics() {
    loadCampaignStatistics(
      this.campaignService,
      stats => {
        this.statistics = stats;
        this.updateStats();
      },
      value => {
        this.loading = value;
      }
    );
  }

  /**
   * Populate the component's campaignStats array from this.statistics if available.
   * @example
   * pdateStats()
   * // undefined
   * @param {Object} context - The component instance (this) containing the statistics property.
   * @returns {void} Updates this.campaignStats in-place or returns early if statistics is falsy.
   */
  updateStats() {
    if (!this.statistics) return;

    this.campaignStats = [
      {
        title: 'Total Campaigns',
        value: this.statistics.statistics.total_campaigns,
        color: '#73A0E9',
        iconColor: '#8FB7F6',
        backgroundImage: 'assets/dashboard/h-1.png',
      },
      {
        title: 'Active Campaigns',
        value: this.statistics.statistics.active_campaigns,
        color: '#B473E9',
        iconColor: '#B473E9',
        backgroundImage: 'assets/dashboard/h-2.png',
      },
      {
        title: 'Scheduled Campaigns',
        value: this.statistics.statistics.scheduled_campaigns,
        color: '#E973C6',
        iconColor: '#E973C6',
        backgroundImage: 'assets/dashboard/h-3.png',
      },
      {
        title: 'Completed Campaigns',
        value: this.statistics.statistics.completed_campaigns,
        color: '#E4A167',
        iconColor: '#EAA468',
        backgroundImage: 'assets/dashboard/h-4.png',
      },
    ];
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
